'use strict';
/* ==========================================================================
   levels/jingle.js — step "jingle" (The Jingle, any): DO IT TO BOTH SIDES, the 1950s doo-wop jingle as a notebook
   lyric video (DESIGN.md §5.4). Model: lessons/tails-due/levels/jingle.js (same cast, same pack, same structure).

   ► The song: assets/music/jingle.m4a (the one ACE-Step take, doowop_7) + assets/music/jingle_words.json
     (tools/song_timing.py): the TRUE lyric lines of content/jingle.json with word times, and the beat grid {bpm,
     offset}. NOTHING here hard-codes a time: acts are found by the lines' SECTIONS (Intro, Verse 1, Chorus, Verse 2,
     Bridge, Chorus, Outro) and every picture beat is keyed to a sung word (S.W(line, word)) or to the beat grid, so a
     new take with the same lyric sheet needs no code change. The karaoke shows only the words of jingle_words.json.

   The whole frame is a pure function of SONG TIME t (the shared <audio> element's clock while it plays; a wall clock
   while audio is locked or missing, or in the tests at __timeScale > 1), so seeking, pausing and stills are exact.

   EVERY NUMBER on screen comes from core-local/scale.js (see nums()): the equations are Scale.VIDEO's (s1 socks
   2x + 6 = 20, s3 gift bags 3(x + 2) = 24, s4 °F → °C 1.8C + 32 = −4), the worked lines are Scale.steps (their
   acts and properties too), the checks Scale.check, "can't hold" is Scale.canHold, what sits on the pans is
   Scale.contents, and the beam's angle is Scale.tilt(left ounces, right ounces) of what is on the pans at that moment
   (honest: it tips toward the heavier pan, level when equal). On top of that the beam ROCKS on the beat (a decoration,
   ±ROCK degrees, one swing per beat) in the verses and the bridge; each chorus damps the rocking out from its first
   word to "level", after which the beam is exactly level (tilt 0) for the rest of the chorus. The outro is level.

   Acts (by section; Verse 2 is split in two pages):
     intro    the title, the empty balance rocking on the beat, "keep it level"
     verse1   s1 on the balance: two boxes and six weights land on the left, twenty on the right (the beam follows the
              model: tipped left until the right pan catches up, level at 20); "take six off both": six weights float
              off each pan, 14 left; "halve both pans": one box vs 7; the worked lines write on (Scale.steps undo)
     chorus1  the hook line big in red pen; the balance (one box vs 7 weights) settles level; "x = 7" on "fine"
     verse2   s1 two ways, side by side: subtract first (undo) | divide first (divideFirst); both land on x = 7
     bags     s3: three gift bags (x + 2 each) vs 24; "the three hits the tissue": red arrows from the 3 to the x and
              to the 2; the distribute lines (Scale.steps distribute): x = 6, one candle = 6 ounces
     bridge   s4: the balance grayed (Scale.canHold false) with the "can't weigh negatives" sticky; the rule keeps
              working (Scale.steps undo: C = −20); the check (Scale.check): −4 = −4, ticked
     chorus2  the hook big; the gift bags vs 24 settle level; "x = 6" on "fine"
     outro    "Do it to both sides" big; the balance (s1 as written) level; the BALANCED stamp on "sides"
   Karaoke (the index card at the bottom): the current line written on word by word, the sung word under the
   highlighter, the hook words (content/jingle.json "hook") in red pen, the next line faint below.
   Jay (left) and Ms. Rivera (right) are small line-art figures bobbing on the beat; they point on key words.
   Controls: click / Space play-pause, ↺ restart (R), ⌂ map, ← → seek 5 s, click or drag the strip at the bottom.
   "Finish" appears once ≥ 90% of the song has been heard; the end of a listen with ≥ 90% heard stamps the step.
   Portrait phone: the stage on top, Dock.karaoke + ↺ ▶ ⌂ + the strip below.
   Long music (docs/WEBSITE.md §8.4): lesson.json preload.stream names the .m4a, so it plays through the shared
   <audio> element (Audio.media), never decoded into memory.

   QA hooks: sc.song (S(i), E(i), W(i, word), beats, sec), sc.plan.acts, sc.nums, sc.debugAt(t) (freeze at song time
   t), sc.now(), sc.play(t), sc.pause(), sc.toggle(), sc.seek(t), sc.restart(), sc.done(), sc.finish(),
   sc.lyricAt(t), sc.activeWord(t), sc.actAt(t), sc.balanceAt(t) ({tilt, model, rock, gray, left, right}),
   sc.heard, sc.ended, sc.audioReady, sc.canFinish(); Levels.jingle.nums(), Levels.jingle.balanceAt(song, plan, t).
   ========================================================================== */
(function () {
  const STEP = 'jingle';
  const AUDIO = 'music/jingle.m4a', WORDS = 'music/jingle_words.json';
  const KEY = 'jingle:balance';
  const PN = NB.PAL, FONT = NB.FONT;
  const BAND_Y = 846, CUR_Y = 934, NEXT_Y = 1012, CUR_SIZE = 62, NEXT_SIZE = 38;
  const TURN = 0.45;                                   // the page turn between acts (s)
  const FINISH_AT = 0.9;                               // share of the song heard before "Finish" / the stamp
  const ROCK = 3.5;                                    // the beat rock's size (degrees; decoration)
  const CREDIT = 'AI-generated jingle · the math is real';
  const TITLE = 'Do It to Both Sides';
  const STAMP = 'BALANCED';

  /* ---------------- the numbers (scale.js only) ---------------- */
  const num = q => Scale.num(q);
  const N_ = q => Scale.toNumber(q);
  let NUMS = null;
  function nums() {
    if (NUMS) return NUMS;
    const V = Scale.VIDEO;
    const one = (e, methods) => {
      const eq = Scale.mkEq(e), x = Scale.solve(eq), c = Scale.contents(eq), o = { eq, x, xn: N_(x), text: Scale.text(eq), side: Scale.side(eq), c, hold: Scale.canHold(eq), check: Scale.check(eq, x) };
      for (const m of methods) o[m] = Scale.steps(eq, m);
      return o;
    };
    const s1 = one(V.s1.eq, ['undo', 'divideFirst']), s3 = one(V.s3.eq, ['distribute']), s4 = one(V.s4.eq, ['undo']);
    // verse 1's pans after each step: "take six off both" (q off each pan), "halve both pans" (÷ p)
    s1.p = N_(s1.eq.p); s1.q = N_(s1.eq.q); s1.r = N_(s1.eq.r);
    s1.afterSub = s1.r - s1.q;                         // 14
    s3.p = N_(s3.eq.p); s3.q = N_(s3.eq.q); s3.r = N_(s3.eq.r);
    NUMS = { s1, s3, s4 };
    return NUMS;
  }

  /* ---------------- song timing ---------------- */
  const normW = w => String(w).toLowerCase().replace(/[^a-z0-9]/g, '');
  class Song {
    constructor(d) {
      this.d = d; this.lines = d.lines; this.dur = d.duration || 80;
      const b = d.beats || {};
      this.bpm = b.bpm || 112; this.bt = 60 / this.bpm; this.off = b.offset || 0;
      this.items = d.lines.map(l => ({ ...l, kind: 'line' })).concat((d.extras || []).map((x, j) => ({ ...x, i: 100 + j }))).sort((a, c) => a.s - c.s);
      this.sec = [];
      d.lines.forEach((l, i) => { const last = this.sec[this.sec.length - 1]; if (last && last.name === l.section) last.lines.push(i); else this.sec.push({ name: l.section, lines: [i] }); });
    }
    section(name, n = 1) { const s = this.sec.filter(x => x.name === name)[n - 1]; return s ? s.lines : []; }
    S(i) { return this.lines[i] ? this.lines[i].s : 1e6; }
    E(i) { return this.lines[i] ? this.lines[i].e : 1e6; }
    /* start of the n-th word of line i that matches `word` (exact, then prefix); the line start if none */
    W(i, word, n = 1) {
      const l = this.lines[i]; if (!l) { if (window.__songCueMiss) window.__songCueMiss.push('?' + i + ':' + word); return 1e6; }
      const k = normW(word);
      for (const pass of [w => normW(w.w) === k, w => normW(w.w).startsWith(k)]) {
        let c = 0;
        for (const w of l.words) if (pass(w) && ++c === n) return w.s;
      }
      if (window.__songCueMiss) window.__songCueMiss.push(i + ':' + word);
      return l.s;
    }
    /* the end of that word */
    WE(i, word, n = 1) {
      const s = this.W(i, word, n), l = this.lines[i];
      const w = l && l.words.find(x => x.s === s);
      return w ? w.e : s + 0.3;
    }
    beatPos(t) { return (t - this.off) / this.bt; }
    sinceBeat(t) { const p = this.beatPos(t); return (p - Math.floor(p)) * this.bt; }
    pulse(t, k = 9) { return t < this.off - 0.01 ? 0 : Math.exp(-this.sinceBeat(t) * k); }
  }

  /* ---------------- small drawing helpers ---------------- */
  const fontOk = () => !document.fonts || document.fonts.status === 'loaded';
  const widths = new Map();
  function tw(str, font) {
    const k = font + '|' + str; let w = widths.get(k);
    if (w === undefined) { g.save(); g.font = font; w = g.measureText(str).width; g.restore(); if (fontOk()) { if (widths.size > 2000) widths.clear(); widths.set(k, w); } }
    return w;
  }
  const hand = (size, w = 700) => `${w} ${size}px ${FONT.hand}`;
  function rrect(x, y, w, h, r) { g.beginPath(); g.moveTo(x + r, y); g.arcTo(x + w, y, x + w, y + h, r); g.arcTo(x + w, y + h, x, y + h, r); g.arcTo(x, y + h, x, y, r); g.arcTo(x, y, x + w, y, r); g.closePath(); }
  function alpha(a, fn) { if (a <= 0.001) return; g.save(); g.globalAlpha *= Math.min(1, a); try { fn(); } finally { g.restore(); } }
  const say = (text, x, y, t0, o = {}) => NB.write(text, x, y, { size: o.size || 56, weight: o.weight || 700, align: o.align || 'center', t0, dur: o.dur, color: o.color, font: o.font });
  /* a label on a small paper backing (x = left, y = middle) so it reads over grid lines */
  function plabel(text, x, y, t0, o = {}) {
    if (T < t0) return;
    const size = o.size || 32, f = hand(size), w = tw(text, f), col = o.color || PN.ink;
    const x0 = o.align === 'right' ? x - w : o.align === 'center' ? x - w / 2 : x;
    alpha(P(T, t0, t0 + 0.2), () => { g.fillStyle = PN.page; rrect(x0 - 8, y - size * 0.62, w + 16, size * 1.24, 8); g.fill(); });
    NB.write(text, x0, y, { size, weight: 700, color: col, t0, dur: 0.3 });
  }
  /* the highlighter behind one part of a centred line of handwriting */
  function hiPart(text, part, cx, y, size, t0) {
    const i = text.indexOf(part); if (i < 0) return;
    const f = hand(size), x0 = cx - tw(text, f) / 2 + tw(text.slice(0, i), f), w = tw(part, f);
    NB.highlight(x0 - size * 0.08, y - size * 0.34, w + size * 0.16, size * 0.7, { t0, dur: 0.3 });
  }
  /* a worked line: the act in small red pen above, the line in ink, its property in small gray below */
  function workLine(step, x, y, tAct, tLine, o = {}) {
    const size = o.size || 54;
    if (step.act && tAct != null) NB.write(step.act, x, y - size * 0.95, { size: o.actSize || 28, weight: 700, color: PN.redpen, align: 'center', t0: tAct, dur: 0.4 });
    say(step.text, x, y, tLine, { size, dur: o.dur });
    if (step.property) NB.write(step.property, x, y + size * 0.72, { font: 'ui', size: o.propSize || 22, weight: 700, color: PN.pencil, align: 'center', t0: tLine + 0.3, dur: 0.3 });
  }
  /* a red-pen arrow (an arc from a to b, bulging up by h), drawn on from t0 */
  function arrow(ax, ay, bx, by, h, t0, dur = 0.35) {
    if (T < t0) return;
    const pts = [], n = 24;
    for (let i = 0; i <= n; i++) { const u = i / n; pts.push([ax + (bx - ax) * u, ay + (by - ay) * u - h * 4 * u * (1 - u)]); }
    const p = E.outCubic(P(T, t0, t0 + dur));
    NB.pen(pts, { w: 5, color: PN.redpen, prog: p, step: 6 });
    if (p >= 1) {
      const [px, py] = pts[n - 2], ang = Math.atan2(by - py, bx - px);
      NB.pen([[bx - 18 * Math.cos(ang - 0.5), by - 18 * Math.sin(ang - 0.5)], [bx, by], [bx - 18 * Math.cos(ang + 0.5), by - 18 * Math.sin(ang + 0.5)]], { w: 5, color: PN.redpen, prog: 1 });
    }
  }
  /* an ink eighth note (the karaoke's instrumental gap; drawn, not a font glyph) */
  function inkNote(x, y, s, dbl) {
    g.save(); g.translate(x, y); g.scale(s, s); g.fillStyle = PN.ink; g.strokeStyle = PN.ink; g.lineWidth = 4; g.lineCap = 'round';
    const heads = dbl ? [[-16, 14], [20, 6]] : [[0, 14]];
    heads.forEach(([hx, hy]) => { g.beginPath(); g.ellipse(hx, hy, 11, 8, -0.4, 0, TAU); g.fill(); g.beginPath(); g.moveTo(hx + 9, hy - 2); g.lineTo(hx + 9, hy - 46); g.stroke(); });
    g.lineWidth = 6; g.beginPath();
    if (dbl) { g.moveTo(heads[0][0] + 9, heads[0][1] - 46); g.lineTo(heads[1][0] + 9, heads[1][1] - 46); }
    else { g.moveTo(9, -32); g.quadraticCurveTo(26, -20, 22, -4); }
    g.stroke(); g.restore();
  }

  /* ---------------- the balance on the beat ---------------- */
  /* the beat rock: one swing per beat (extreme on the beat), amp in degrees */
  const rock = (S, t, amp) => (amp > 0 && t >= S.off ? amp * Math.cos(Math.PI * S.beatPos(t)) : 0);
  /* pans: {left, right} = BalanceKit contents; model tilt from the ounces on them (x = the solution) */
  function ounces(c, x, q) { return (c.boxes || 0) * x + (c.bags || 0) * (x + (q || 0)) + (c.weights || 0); }
  /* the ghosts of what was just taken off a pan: k weights (and boxes) floating up and fading (pure in t) */
  function ghosts(pan, s, weights, boxes, t, t0, v) {
    if (t < t0 || t > t0 + 0.9) return;
    const u = E.outCubic(P(t, t0, t0 + 0.9)), dy = -40 - 170 * u;
    alpha(1 - u, () => {
      const ww = BalanceKit.GEOM.weight * s * 1.1, bw = BalanceKit.GEOM.box * s * 1.1;
      const total = boxes * bw + weights * ww, x0 = pan.x - total / 2;
      for (let i = 0; i < boxes; i++) BalanceKit.box(x0 + (i + 0.5) * bw, pan.y + dy, s, v);
      for (let i = 0; i < weights; i++) BalanceKit.weight(x0 + boxes * bw + (i + 0.5) * ww, pan.y + dy, s);
    });
  }

  /* each act's balance at song time t: {x, y, s, left, right, model (deg), amp, gray, v, ghosts?} (pure in t) */
  const BAL = {
    intro(S, t) { return { x: 960, y: 800, s: 0.78, left: {}, right: {}, model: 0, amp: ROCK * E.inOutSine(P(t, 0.3, 1.5)) }; },
    verse1(S, t, A) {
      const n = nums().s1, L = A.lines, c = n.c, x = n.xn;
      const tTwo = S.W(L[0], 'Two'), tBoxes = S.W(L[0], 'boxes'), tSix = S.W(L[0], 'six'), tSide = S.WE(L[0], 'side');
      const tTw = S.W(L[1], 'Twenty'), tRight = S.WE(L[1], 'right'), tOff = S.W(L[2], 'off'), tHalve = S.W(L[3], 'Halve');
      const left = { boxes: t < tTwo ? 0 : t < tBoxes ? 1 : c.left.boxes, weights: Math.floor(c.left.weights * P(t, tSix, Math.max(tSix + 0.3, tSide)) + 1e-9) };
      const right = { weights: Math.floor(c.right.weights * P(t, tTw, Math.max(tTw + 0.3, tRight)) + 1e-9) };
      const B = { x: 700, y: 800, s: 0.8, left, right, amp: ROCK, v: n.eq.v, ghost: null };
      if (t >= tOff) { left.weights = 0; right.weights = n.afterSub; B.ghost = { t0: tOff, left: { w: n.q, b: 0 }, right: { w: n.q, b: 0 } }; }
      if (t >= tHalve) {
        left.boxes = c.left.boxes / n.p; right.weights = n.afterSub / n.p;
        B.ghost = { t0: tHalve, left: { w: 0, b: c.left.boxes - left.boxes }, right: { w: n.afterSub - right.weights, b: 0 } };
      }
      B.model = Scale.tilt(ounces(left, x), ounces(right, x));
      return B;
    },
    chorus(S, t, A) {
      const N = nums(), L = A.lines, first = A.n === 1;
      const tDo = L.length ? S.W(L[0], 'Do') : A.at, tLevel = L.length > 2 ? S.WE(L[2], 'level') : tDo + 3;
      const amp = t < tDo ? ROCK : t >= tLevel ? 0 : ROCK * (1 - E.inOutSine(P(t, tDo, tLevel)));
      let left, right, x, q = 0, v;
      if (first) { const n = N.s1; left = { boxes: 1 }; right = { weights: n.xn }; x = n.xn; v = n.eq.v; }
      else { const n = N.s3; left = { ...n.c.left }; right = { ...n.c.right }; x = n.xn; q = n.q; v = n.eq.v; }
      return { x: 960, y: 800, s: 0.76, left, right, amp, v, model: Scale.tilt(ounces(left, x, q), ounces(right, x, q)) };
    },
    verse2(S, t) { const n = nums().s1; return { x: 960, y: 810, s: 0.46, left: { ...n.c.left }, right: { ...n.c.right }, amp: ROCK, v: n.eq.v, model: Scale.tilt(ounces(n.c.left, n.xn), ounces(n.c.right, n.xn)) }; },
    bags(S, t, A) {
      const n = nums().s3, L = A.lines, c = n.c;
      const tTh = S.W(L[0], 'Three'), tBags = S.WE(L[0], 'bags'), t24 = S.W(L[0], 'twentyfour'), tAll = S.WE(L[0], 'all');
      const left = { ...c.left, bags: Math.min(c.left.bags, Math.floor(c.left.bags * P(t, tTh, Math.max(tTh + 0.3, tBags)) + 1e-9)) };
      const right = { weights: Math.floor(c.right.weights * P(t, t24, Math.max(t24 + 0.3, tAll)) + 1e-9) };
      return { x: 640, y: 800, s: 0.8, left, right, amp: ROCK, v: n.eq.v, model: Scale.tilt(ounces(left, n.xn, n.q), ounces(right, n.xn, n.q)) };
    },
    bridge(S, t) { return { x: 540, y: 790, s: 0.68, left: {}, right: {}, amp: ROCK, gray: !nums().s4.hold, model: 0 }; },
    outro(S, t) { const n = nums().s1; return { x: 960, y: 800, s: 0.76, left: { ...n.c.left }, right: { ...n.c.right }, amp: 0, v: n.eq.v, model: Scale.tilt(ounces(n.c.left, n.xn), ounces(n.c.right, n.xn)) }; },
  };
  function balanceState(S, t, A) {
    const B = (BAL[A.bal] || BAL.intro)(S, t, A);
    B.rock = rock(S, t, B.amp);
    B.tilt = B.model + B.rock;
    return B;
  }
  function drawBalance(S, t, A) {
    const B = balanceState(S, t, A);
    const geo = BalanceKit.draw(B.x, B.y, { s: B.s, tilt: B.tilt, left: B.left, right: B.right, v: B.v || 'x', gray: B.gray });
    if (B.ghost) {
      const G = B.ghost;
      ghosts(geo.left, B.s, G.left.w, G.left.b, t, G.t0, B.v);
      ghosts(geo.right, B.s, G.right.w, G.right.b, t, G.t0, B.v);
    }
    return { B, geo };
  }

  /* ======================================================================
     act: intro — the title, the empty balance rocking on the beat
     ====================================================================== */
  function actIntro(S, t, A) {
    NB.ground();
    NB.title(TITLE, 960, 230, { size: 108, t0: 0.15, hi: 'Both Sides' });
    const { geo } = drawBalance(S, t, A);
    const L = A.lines, tK = L.length ? S.W(L[0], 'keep') : 1.5, tL = L.length ? S.W(L[0], 'level') : 2;
    if (t > tK) NB.note('a two-pan balance', 1330, 470, -0.04, { t0: tK, size: 40 });
    if (t > tL) NB.note('keep it level', geo.pivot.x + 110, geo.pivot.y - 90, -0.05, { t0: tL, size: 38 });
  }

  /* ======================================================================
     act: verse 1 — s1 on the balance: 2x + 6 = 20, six off both, halve both pans, x = 7
     ====================================================================== */
  function actVerse1(S, t, A) {
    NB.ground();
    const n = nums().s1, L = A.lines, CX = 1340;
    const { geo } = drawBalance(S, t, A);
    const tSix = S.W(L[0], 'six'), tRight = S.WE(L[1], 'right'), tLev = S.W(L[1], 'level');
    // the equation writes on as the pans fill: "2x + 6" on the left, "= 20" once the right pan is full
    if (t > tSix) say(n.text, CX, 300, tSix, { size: 70, dur: Math.max(0.5, tRight + 0.2 - tSix) });
    if (t > tLev) NB.note('level', geo.pivot.x + 90, geo.pivot.y - 80, -0.05, { t0: tLev, size: 40 });
    // take six off both → 2x = 14; halve both pans → x = 7
    const u = n.undo, tTake = S.W(L[2], 'Take'), tNow = S.W(L[2], 'now'), tFt = S.WE(L[2], 'fourteen');
    workLine(u[0], CX, 450, tTake, tNow, { dur: Math.max(0.4, tFt - tNow) });
    const tHalve = S.W(L[3], 'Halve'), tSev = S.W(L[3], 'seven'), tWt = S.W(L[3], 'weight');
    workLine(u[1], CX, 620, tHalve, tSev, { size: 64, dur: 0.4 });
    if (t > tWt) hiPart(u[1].text, num(n.x), CX, 620, 64, tWt);
    if (t > tWt) NB.note(`${n.eq.v} = the weight of ONE box`, CX, 740, -0.02, { t0: tWt, size: 34, align: 'center' });
  }

  /* ======================================================================
     act: chorus — the hook big; the balance damps out and sits level; the answer on "fine"
     ====================================================================== */
  function actChorus(S, t, A) {
    NB.ground();
    const L = A.lines, first = A.n === 1, n = first ? nums().s1 : nums().s3;
    // the hook line, big, written as it is sung (the current chorus line)
    let cur = -1; L.forEach((i, k) => { if (t >= S.S(i) - 0.05) cur = k; });
    if (cur >= 0) {
      const i = L[cur], text = S.lines[i].text, ws = S.lines[i].words, size = 92;
      const w = tw(text, hand(size)), sz = w > 1100 ? Math.floor(size * 1100 / w) : size;
      NB.write(text, 960, 262, { size: sz, weight: 700, align: 'center', color: PN.redpen, t0: S.S(i) - 0.05, dur: Math.max(0.3, ws[ws.length - 1].s + 0.2 - S.S(i)) });
    }
    const { geo } = drawBalance(S, t, A);
    if (!L.length) return;
    // "both sides": a red underline under each pan; "Every part": a ring round each pan's load
    const tBoth = S.W(L[0], 'both'), tPart = L.length > 1 ? S.W(L[1], 'part') : 1e6;
    const pw = BalanceKit.GEOM.pan * 0.76;
    for (const pan of [geo.left, geo.right]) {
      if (t > tBoth) NB.pen([[pan.x - pw / 2, pan.y + 52], [pan.x + pw / 2, pan.y + 52]], { w: 6, color: PN.redpen, prog: E.outCubic(P(t, tBoth, tBoth + 0.35)) });
      if (t > tPart) NB.circle(pan.x, pan.y - 50, pw * 0.56, 80, { t0: tPart, dur: 0.4 });
    }
    // "Keep the scale level": the dial circled once it has settled
    const tLevel = L.length > 2 ? S.WE(L[2], 'level') : 1e6;
    if (t > tLevel) { NB.circle(geo.pivot.x, geo.pivot.y - 44, 70, 46, { t0: tLevel, dur: 0.35, color: PN.ink }); NB.note('level', geo.pivot.x + 96, geo.pivot.y - 96, -0.05, { t0: tLevel, size: 40 }); }
    // "And the answer comes out fine": the answer from the model
    const tFine = L.length > 3 ? S.W(L[3], 'answer') : 1e6;
    if (t > tFine) plabel(`${n.eq.v} = ${num(n.x)}`, 1440, 560, tFine, { size: 60, align: 'center', color: PN.redpen });
    if (t > tFine + 0.3) NB.note(first ? 'one box' : 'one candle', 1440, 630, -0.03, { t0: tFine + 0.3, size: 34, align: 'center', color: PN.pencil });
  }

  /* ======================================================================
     act: verse 2 (first half) — s1 two ways: subtract first | divide first; the same x
     ====================================================================== */
  const COLS = [470, 1450];
  function actVerse2(S, t, A) {
    NB.ground();
    const n = nums().s1, L = A.lines;
    drawBalance(S, t, A);
    const tDiv = S.W(L[0], 'Divide'), tSub = S.W(L[0], 'subtract'), tYou = S.W(L[0], 'you'), tCh = S.W(L[0], 'choose');
    const L1 = L.length > 1 ? L[1] : L[0];
    const tHalve = S.W(L1, 'Halve'), tPart = S.W(L1, 'part'), tAnd = S.W(L1, 'and'), tNever = S.W(L1, 'never'), tLose = S.W(L1, 'lose');
    // headers: "divide first" on "Divide", "subtract first" on "subtract"
    NB.write('subtract first', COLS[0], 250, { font: 'ui', size: 34, weight: 700, align: 'center', t0: tSub, dur: 0.3 });
    NB.write('divide first', COLS[1], 250, { font: 'ui', size: 34, weight: 700, align: 'center', t0: tDiv, dur: 0.3 });
    for (const x of COLS) say(n.text, x, 330, A.at + 0.2, { size: 58, dur: 0.5 });
    // subtract first (Scale.steps undo)
    const u = n.undo;
    workLine(u[0], x0(0), 470, tSub, tSub + 0.35, { size: 52 });
    workLine(u[1], x0(0), 620, tYou, tCh, { size: 52 });
    // divide first (Scale.steps divideFirst): "Halve every part"
    const d = n.divideFirst;
    workLine(d[0], x0(1), 470, tHalve, tPart, { size: 52 });
    workLine(d[1], x0(1), 620, tAnd, tNever, { size: 52 });
    // "you never lose": both answers ringed, the same x
    if (t > tLose) {
      for (let k = 0; k < 2; k++) hiPart(k ? d[1].text : u[1].text, num(n.x), COLS[k], 620, 52, tLose + k * 0.12);
      NB.note(`both ways: ${n.eq.v} = ${num(n.x)}`, 960, 400, -0.03, { t0: tLose, size: 44, align: 'center', dur: 0.3 });
    }
    function x0(k) { return COLS[k]; }
  }

  /* ======================================================================
     act: bags (verse 2, second half) — s3: 3(x + 2) = 24; the three hits the tissue; x = 6
     ====================================================================== */
  function actBags(S, t, A) {
    NB.ground();
    const n = nums().s3, L = A.lines, CX = 1320;
    drawBalance(S, t, A);
    const tCand = S.W(L[0], 'candles'), tAll = S.WE(L[0], 'all');
    const EQY = 290, EQS = 70;
    if (t > tCand) say(n.text, CX, EQY, tCand, { size: EQS, dur: Math.max(0.5, tAll - tCand) });
    if (L.length < 2) return;
    const L1 = L[1], tThree = S.W(L1, 'three'), tTis = S.W(L1, 'tissue'), tSix = S.W(L1, 'six'), tOz = S.W(L1, 'ounces'), tThat = S.W(L1, 'thats');
    // "the three hits the tissue": red arrows from the 3 to the x and to the 2
    const f = hand(EQS), x0 = CX - tw(n.text, f) / 2, at = ch => { const i = n.text.indexOf(ch); return x0 + tw(n.text.slice(0, i), f) + tw(ch, f) / 2; };
    const p3 = at(num(n.eq.p)), px = at(n.eq.v), pq = at(num(n.eq.q) + ')');
    arrow(p3, EQY - 40, px, EQY - 42, 36, tThree);
    arrow(p3, EQY - 40, pq - 8, EQY - 42, 62, tTis);
    // Scale.steps distribute: 3x + 6 = 24, 3x = 18, x = 6
    const st = n.distribute;
    workLine(st[0], CX, 440, tThree, tTis + 0.2, { size: 52, actSize: 26 });
    workLine(st[1], CX, 575, tSix - 0.25, tSix, { size: 52, actSize: 26, dur: 0.35 });
    workLine(st[2], CX, 710, tOz - 0.25, tOz, { size: 60, actSize: 26, dur: 0.35 });
    if (t > tThat) { hiPart(st[2].text, num(n.x), CX, 710, 60, tThat); NB.note(`one candle: ${num(n.x)} ounces`, CX, 808, -0.02, { t0: tThat, size: 34, align: 'center' }); }
  }

  /* ======================================================================
     act: bridge — s4: can't hold a negative; the rule keeps working; the check
     ====================================================================== */
  function actBridge(S, t, A) {
    NB.ground();
    const n = nums().s4, L = A.lines, CX = 1310;
    drawBalance(S, t, A);
    const tHold = S.W(L[0], 'hold'), tNeg = S.W(L[0], 'negative');
    if (t > tHold) say(n.text, CX, 280, tHold, { size: 66, dur: 0.6 });
    if (!n.hold && t > tNeg) {
      const st = NB.sticky("can't weigh negatives", 360, 250, { w: 330, rot: -0.05, t0: tNeg, size: 38 });
      void st;
    }
    if (L.length < 2) return;
    // "But the rule keeps working": Scale.steps undo
    const u = n.undo, tRule = S.W(L[1], 'rule'), tKeeps = S.W(L[1], 'keeps'), tWork = S.W(L[1], 'working'), tRule2 = S.W(L[1], 'rule', 2), tGreat = S.W(L[1], 'great');
    workLine(u[0], CX, 420, tRule, tKeeps, { size: 54 });
    workLine(u[1], CX, 555, tWork, tRule2, { size: 58 });
    if (t > tGreat) hiPart(u[1].text, num(n.x), CX, 555, 58, tGreat);
    if (L.length < 4) return;
    // "Put your answer back in, and check it right": Scale.check
    const ck = n.check, tBack = S.W(L[2], 'back'), tCheck = S.W(L[2], 'check'), tMatch = S.W(L[3], 'match'), tSleep = S.W(L[3], 'sleep');
    NB.write(ck.text, CX, 690, { size: 40, weight: 700, align: 'center', t0: tBack, dur: Math.max(0.5, tCheck + 0.4 - tBack) });
    if (t > tMatch) {
      const eq = `${num(ck.left)} = ${num(ck.right)}`;
      say(eq, CX, 770, tMatch, { size: 56, dur: 0.4 });
      if (ck.ok && t > tSleep) NB.tick(CX + tw(eq, hand(56)) / 2 + 50, 770, { t0: tSleep, s: 1.4 });
    }
  }

  /* ======================================================================
     act: outro — the hook big; the balance level; BALANCED on "sides"
     ====================================================================== */
  function actOutro(S, t, A) {
    NB.ground();
    const L = A.lines, n = nums().s1;
    const text = L.length ? S.lines[L[0]].text : TITLE, tDo = L.length ? S.S(L[0]) : A.at, tSides = L.length ? S.W(L[0], 'sides') : A.at + 1;
    NB.write(text, 960, 280, { size: 100, weight: 700, align: 'center', color: PN.redpen, t0: tDo - 0.05, dur: Math.max(0.4, tSides + 0.3 - tDo) });
    const { geo } = drawBalance(S, t, A);
    if (t > tDo + 0.3) plabel(n.text, geo.pivot.x, geo.pivot.y - 150, tDo + 0.3, { size: 48, align: 'center' });
    const tSt = Math.min(tSides + 0.3, S.dur - 0.8);
    if (t > tSt) UI.stamp(STAMP, 1480, 420, { s: E.outBack(P(t, tSt, tSt + 0.3), 1.6), rot: -0.12 });
  }

  /* ---------------- the acts' plan (by section, from the take's own line times) ---------------- */
  function plan(S) {
    const b = i => (i > 0 ? Math.max(S.E(i - 1) - 0.1, S.S(i) - 0.35) : 0);   // never before the previous line's last word
    const acts = [];
    const add = (id, draw, lines, o = {}) => { if (id !== 'intro' && !lines.length) return; acts.push({ id, draw, lines, bal: id, at: id === 'intro' ? 0 : b(lines[0]), ...o }); };
    const v2 = S.section('Verse 2'), half = Math.ceil(v2.length / 2);
    add('intro', actIntro, S.section('Intro'));
    add('verse1', actVerse1, S.section('Verse 1'));
    add('chorus1', actChorus, S.section('Chorus', 1), { n: 1, bal: 'chorus' });
    add('verse2', actVerse2, v2.slice(0, half));
    add('bags', actBags, v2.slice(half));
    add('bridge', actBridge, S.section('Bridge'));
    add('chorus2', actChorus, S.section('Chorus', 2), { n: 2, bal: 'chorus' });
    add('outro', actOutro, S.section('Outro'));
    acts.sort((a, c) => a.at - c.at);
    acts.forEach((a, i) => { a.end = i + 1 < acts.length ? acts[i + 1].at : S.dur; });
    return { acts, ticks: acts.slice(1).map(a => a.at) };
  }
  function actAt(p, t) { let k = 0; for (let i = 0; i < p.acts.length; i++) if (t >= p.acts[i].at) k = i; return k; }
  /* the balance of the act on screen at song time t (QA) */
  function balanceAt(S, p, t) {
    const A = p.acts[actAt(p, t)], B = balanceState(S, t, A);
    return { act: A.id, tilt: B.tilt, model: B.model, rock: B.rock, amp: B.amp, gray: !!B.gray, left: B.left, right: B.right };
  }
  /* the notebook page turn: the old page slides up off the top (the next page is already underneath) */
  function turn(drawOld, drawNew, t, t0, dur) {
    const p = E.inOutCubic(P(t, t0, t0 + dur));
    drawNew();
    if (p >= 1) return;
    const dy = -1100 * p;
    g.save(); g.beginPath(); g.rect(0, 0, 1920, 1080 + dy); g.clip(); g.translate(0, dy); drawOld(); g.restore();
    g.save(); g.fillStyle = 'rgba(29,43,79,0.12)'; g.fillRect(0, 1080 + dy, 1920, 8); g.restore();
  }
  function drawActs(S, p, t) {
    const k = actAt(p, t), A = p.acts[k];
    const run = i => () => { g.save(); p.acts[i].draw(S, t, p.acts[i]); g.restore(); g.globalAlpha = 1; };
    if (k > 0 && t < A.at + TURN) turn(run(k - 1), run(k), t, A.at, TURN);
    else run(k)();
  }

  /* ---------------- the cast: Jay (left) and Ms. Rivera (right), bobbing on the beat ---------------- */
  const POSE_DX = { rivera_point: -131.5 / 1100, jay_point: 48.5 / 1100 };     // as tails-due (the same art)
  function puppet(name, x, y, h, pose, facing, env, t, rot) {
    const img = (Puppet.POSES[name] || {})[pose] || Puppet.DEFAULT[name] || name, native = Puppet.NATIVE[img] || 0;
    const flip = native !== 0 && Math.sign(native) !== Math.sign(facing);
    Puppet.draw(name, x - (POSE_DX[img] || 0) * h * (flip ? -1 : 1), y, h, { env, facing, t, pose, rot });
  }
  function pointCues(S, p) {
    if (p._pts) return p._pts;
    const jay = [], riv = [], A = id => p.acts.find(a => a.id === id), L = id => (A(id) ? A(id).lines : []);
    const v1 = L('verse1'), v2 = L('verse2'), bg = L('bags'), br = L('bridge'), out = L('outro');
    if (v1.length >= 4) { jay.push(S.W(v1[0], 'boxes')); riv.push(S.W(v1[2], 'Take'), S.W(v1[3], 'Halve')); }
    if (v2.length >= 2) { jay.push(S.W(v2[0], 'Divide')); riv.push(S.W(v2[1], 'Halve')); }
    if (bg.length >= 2) { jay.push(S.W(bg[0], 'Three')); riv.push(S.W(bg[1], 'tissue')); }
    if (br.length >= 3) { jay.push(S.W(br[0], 'negative')); riv.push(S.W(br[1], 'rule'), S.W(br[2], 'check')); }
    for (const id of ['chorus1', 'chorus2']) { const l = L(id); if (l.length) riv.push(S.W(l[0], 'both')); }
    if (out.length) { jay.push(S.W(out[0], 'sides')); riv.push(S.W(out[0], 'both')); }
    return (p._pts = { jay, riv });
  }
  function cast(S, p, t, docked) {
    const pu = S.pulse(t, 7), bar = Math.floor(S.beatPos(t) / 4), sway = (bar % 2 ? 1 : -1) * 0.02, bob = 10 * pu;
    const cues = pointCues(S, p), near = arr => arr.some(x => t > x && t < x + 1.0);
    const y = docked ? 1080 : BAND_Y + 20, h = docked ? 380 : 330;
    puppet('jay', 190, y - bob, h, near(cues.jay) ? 'point' : null, 1, 0.15 + 0.85 * pu, t, sway);
    puppet('rivera', 1760, y - bob, h * 1.05, near(cues.riv) ? 'point' : null, -1, 0.15 + 0.85 * pu, t, -sway);
  }

  /* ---------------- karaoke (only the words of jingle_words.json) ---------------- */
  /* the hook words (red pen) in the choruses and the outro: content/jingle.json "hook" lines */
  let HOOKS = null;
  const hooksText = () => (((Game.content || {}).jingle || {}).hook || []);
  const hooks = () => HOOKS || (HOOKS = (((Game.content || {}).jingle || {}).hook || []).map(l => l.split(/\s+/).map(normW).filter(Boolean)));
  function hookIdx(item) {
    const ws = item.words, out = new Set();
    if (item.section !== 'Chorus' && item.section !== 'Outro') return out;
    for (const h of hooks()) for (let k = 0; k + h.length <= ws.length; k++) if (h.every((w, j) => normW(ws[k + j].w) === w)) h.forEach((_, j) => out.add(k + j));
    return out;
  }
  const layouts = new Map();
  function layout(item, size) {
    const key = item.i + '|' + item.s + '|' + size;
    if (layouts.has(key)) return layouts.get(key);
    const f = hand(size), sp = tw(' ', f), hk = hookIdx(item);
    let x = 0;
    const toks = item.words.map((w, j) => { const ww = tw(w.w, f), tk = { text: w.w, s: w.s, e: w.e, x, w: ww, hook: hk.has(j) }; x += ww + sp; return tk; });
    const out = { toks, width: x - sp, size };
    if (fontOk()) layouts.set(key, out);
    return out;
  }
  const wordEnd = (ws, j) => { const nx = ws[j + 1]; return Math.min(Math.max(ws[j].e, ws[j].s + 0.18) + 0.05, nx ? nx.s - 0.02 : 1e9); };
  function drawCurrent(item, t, y, maxW = 1640) {
    let size = CUR_SIZE, Lo = layout(item, size);
    if (Lo.width > maxW) { size = Math.floor(size * maxW / Lo.width); Lo = layout(item, size); }
    const x0 = 960 - Lo.width / 2, appear = item.appear ?? item.s - 0.5, a = E.outCubic(P(t, appear, appear + 0.25));
    const yy = y + (1 - a) * 30;
    Lo.toks.forEach((tk, j) => {
      const x = x0 + tk.x, sung = t >= tk.s, active = t >= tk.s - 0.02 && t < wordEnd(Lo.toks, j);
      if (active) NB.highlight(x - 8, yy - size * 0.42, tk.w + 16, size * 0.84, { t0: tk.s - 0.02, dur: 0.12 });
      if (!sung || t < tk.s + 0.3) NB.write(tk.text, x, yy, { size, weight: 700, color: PN.pencilLight, alpha: a });
      if (sung) NB.write(tk.text, x, yy, { size, weight: 700, color: tk.hook ? PN.redpen : PN.ink, t0: tk.s, dur: clamp(tk.e - tk.s, 0.08, 0.3) });
    });
  }
  /* a line comes up 0.5 s before its first word, but never before the previous line has been sung */
  const appearAt = (it, k) => (k === 0 ? it[0].s - 0.5 : Math.min(it[k].s, Math.max(it[k].s - 0.5, it[k - 1].e + 0.05)));
  function lyricAt(song, t) {
    const it = song.items;
    let cur = -1;
    for (let k = 0; k < it.length; k++) if (t >= appearAt(it, k)) cur = k;
    let c = cur >= 0 ? it[cur] : null;
    if (c) c.appear = appearAt(it, cur);
    const nx = it[cur + 1] || null;
    if (c && t > c.e + 2.2 && (!nx || nx.s - t > 1.2)) c = null;       // a long instrumental gap: clear the line
    return { cur: c, next: nx && nx.s - t < 8 ? nx : null };
  }
  function activeWord(song, t) {
    const { cur } = lyricAt(song, t); if (!cur) return null;
    const ws = cur.words;
    for (let j = 0; j < ws.length; j++) if (t >= ws[j].s - 0.02 && t < wordEnd(ws, j)) return { line: cur.i, j, w: ws[j].w, s: ws[j].s, e: ws[j].e };
    return null;
  }
  /* after the last line: the first two hook lines (sung words only: the hook is the chorus's own lines) */
  const endNote = () => hooksText().slice(0, 2).join('. ') + '.';
  function band(song, t) {
    NB.indexCard(40, BAND_Y, 1840, 260, { header: 44, pitch: 78 });
    const { cur, next } = lyricAt(song, t);
    const sec = (cur || next || {}).section;
    if (sec) NB.write(sec.toUpperCase(), 70, BAND_Y + 23, { font: 'ui', size: 22, weight: 700, color: PN.pencil });
    if (cur) drawCurrent(cur, t, CUR_Y);
    if (next) {
      let size = NEXT_SIZE; const w = tw(next.text, hand(size, 400));
      if (w > 1640) size = Math.floor(size * 1640 / w);
      NB.write(next.text, 960, NEXT_Y, { size, weight: 400, color: PN.pencil, align: 'center', alpha: 0.8 });
    } else if (song.items.length && t > song.items[song.items.length - 1].e) {
      NB.write(endNote(), 960, NEXT_Y, { size: 40, weight: 700, color: PN.pencil, align: 'center', t0: song.items[song.items.length - 1].e + 0.3 });
    }
    if (!cur && !next && t > 0.6) {
      const b = ((Math.floor(song.beatPos(t)) % 3) + 3) % 3, pu = song.pulse(t, 6);
      for (let i = 0; i < 3; i++) inkNote(860 + i * 100, CUR_Y - 22 * pu * (i === b ? 1 : 0.15), 1, i === 1);
    }
  }

  /* ======================================================================
     the scene
     ====================================================================== */
  class Jingle extends Scene {
    enter() {
      this.song = null; this.plan = null; this.err = null;
      this.playing = false; this.frozen = false; this.ended = false; this.startedOnce = false;
      this.pos = 0; this.t0ctx = null; this.heard = 0;
      this.audioReady = false; this.completed = false; this.gone = false;
      Audio.stopMusic();
      try { this.nums = nums(); } catch (e) { this.err = 'scale: ' + e.message; console.warn('[jingle]', this.err); }
      const wurl = Assets.url(WORDS);
      const words = (window.Loader ? Loader.json(wurl).then(d => d || Promise.reject(new Error('no timings'))) : fetch(wurl).then(r => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)))))
        .then(d => { this.song = new Song(d); this.plan = plan(this.song); })
        .catch(e => { this.err = String(e && e.message); console.warn('[jingle] no timings', this.err); });
      const audio = (Audio.has(KEY) ? Promise.resolve() : Audio.load({ [KEY]: Assets.url(AUDIO) })).then(() => { this.audioReady = Audio.has(KEY); }, e => { console.warn('[jingle] no audio', e && e.message); });
      Promise.all([words, audio]).then(() => { if (!this.gone && !this.frozen && !this.startedOnce && this.song) this.play(0); });
      if (window.Device) {
        this.onHidden = Device.on('hidden', () => { if (this.playing && !this.gone) { this.pause(); this.resumeOnReturn = true; } });
        this.onResume = Device.on('resume', () => { if (this.resumeOnReturn && !this.gone && Game.current() === this) { this.resumeOnReturn = false; this.play(this.pos); } });
      }
    }
    exit() {
      this.gone = true; Audio.stopGroup('song'); this.playing = false;
      if (this.media) this.media.pause();
      if (window.Device) { Device.off('hidden', this.onHidden); Device.off('resume', this.onResume); }
    }
    get key() { return KEY; }
    back() { Game.replace(new Levels.map()); }
    /* the song clock: the <audio> element while it plays (t0ctx != null = "on the audio clock"), else pos */
    now() {
      if (this.playing && this.t0ctx != null) {
        const m = this.media;
        if (m) return m.playing() ? m.time() : m.el && m.el.ended ? this.song.dur : this.pos;
        if (Audio.running()) return Audio.now() - this.t0ctx;
      }
      return this.pos;
    }
    play(from = this.now()) {
      if (!this.song) return;
      this.startedOnce = true; this.frozen = false; this.ended = false;
      from = clamp(from, 0, this.song.dur - 0.05);
      Audio.stopGroup('song');
      this.pos = from; this.playing = true; this.t0ctx = null;
      if (!this.audioReady || window.__timeScale > 1) return;
      this.media = this.media || Audio.media(KEY);
      if (this.media) {
        const m = this.media, tok = this.playTok = (this.playTok || 0) + 1;
        this.playAt = Game.time; this.blocked = false;
        m.play(from).then(ok => {
          if (tok !== this.playTok || this.gone) return;
          if (ok && this.playing) { this.pos = m.time(); this.t0ctx = 0; }
          else if (!ok) { this.pause(); this.blocked = true; }
        });
      } else if (Audio.running()) {
        const at = Audio.now() + 0.06;
        Audio.schedule(KEY, { at, offset: from, gain: 1, group: 'song', bus: 'voice', fade: 0.03 });
        this.t0ctx = at - from;
      }
    }
    pause() {
      this.pos = this.now(); this.playing = false; this.t0ctx = null; this.playTok = (this.playTok || 0) + 1;
      Audio.stopGroup('song');
      if (this.media) this.media.pause();
    }
    toggle() { if (this.playing) this.pause(); else this.play(this.ended ? 0 : this.pos); Audio.play('paper', { gain: 0.25 }); }
    seek(t) { if (!this.song) return; const was = this.playing; this.pause(); this.pos = clamp(t, 0, this.song.dur); this.ended = false; if (was) this.play(this.pos); }
    restart() { this.heard = 0; this.play(0); }
    /* QA: freeze on song time t (no pause overlay) */
    debugAt(t) { this.pause(); this.frozen = true; this.startedOnce = true; this.ended = false; this.pos = t; }
    /* stamp the step now (QA / integration; ≥ 90% heard does this at the end, or the Finish button) */
    done() { if (this.completed) return; this.completed = true; this.pause(); if (!Game.progress.stamps[STEP]) Game.complete(STEP); }
    canFinish() { return !!this.song && !this.completed && this.heard >= FINISH_AT * this.song.dur; }
    finish() { if (this.canFinish()) { Audio.play('stamp', { gain: 0.6 }); this.done(); } }
    lyricAt(t) { return this.song ? lyricAt(this.song, t) : null; }
    activeWord(t) { return this.song ? activeWord(this.song, t) : null; }
    actAt(t) { return this.plan ? this.plan.acts[actAt(this.plan, t)].id : null; }
    balanceAt(t) { return this.plan ? balanceAt(this.song, this.plan, t) : null; }

    update(dt) {
      if (!this.song || !this.playing || this.scrub) return;
      if (this.t0ctx == null) {
        this.pos += dt * (window.__timeScale || 1);
        const retry = this.audioReady && !(window.__timeScale > 1) &&
          (this.media ? !this.blocked && Game.time - (this.playAt || 0) > 3 : Audio.running());
        if (retry) this.play(this.pos);
      }
      this.heard += dt * (this.t0ctx == null ? (window.__timeScale || 1) : 1);
      if (this.now() >= this.song.dur - 0.05) {
        this.pause(); this.pos = this.song.dur; this.ended = true;
        if (!this.completed && this.heard >= FINISH_AT * this.song.dur) this.done();
      }
    }
    keyDown(k) {
      if (!this.song) return;
      if (k === ' ') this.toggle();
      else if (k === 'r' || k === 'R') this.restart();
      else if (k === 'ArrowRight') this.seek(this.now() + 5);
      else if (k === 'ArrowLeft') this.seek(this.now() - 5);
      else if (k === 'Enter' && this.ended) this.back();
    }
    stripZone() { return Math.max(32, UI.tap(32)); }
    pointerDown(x, y) {
      if (!this.song || this.ended) return;
      if (UI.docked()) { this.toggle(); return; }
      if (UI.inHud(x, y, 3)) return;
      if (this.canFinish() && this._finBox && Math.abs(x - this._finBox.x) < this._finBox.w / 2 && Math.abs(y - this._finBox.y) < this._finBox.h / 2) return;
      if (y > 1080 - this.stripZone()) {
        this.scrub = { was: this.playing };
        if (this.playing) this.pause();
        this.pos = clamp(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); this.ended = false;
        return;
      }
      this.toggle();
    }
    pointerMove(x) { if (this.scrub && this.song) this.pos = clamp(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); }
    pointerUp() {
      if (!this.scrub) return;
      const was = this.scrub.was; this.scrub = null;
      if (was) this.play(this.pos);
    }

    draw() {
      const docked = UI.docked(), title = (Game.content.jingle && Game.content.jingle.title) || TITLE;
      if (!this.song || !this.nums) {
        NB.ground();
        NB.title(title, 960, 420, { size: 110, t0: this.enteredAt + 0.2, hi: 'Both Sides' });
        const msg = this.err ? 'the jingle is missing (' + this.err + ')' : 'warming up the band…';
        NB.write(msg, 960, 580, { font: 'ui', size: 44, color: PN.pencil, align: 'center', t0: this.enteredAt + 0.4 });
        if (docked) { Dock.head(title, ''); Dock.text('jg-wait', [msg], { cls: 'note center' }); this.dockBar(); }
        else this.chrome(null);
        return;
      }
      const t = clamp(this.now(), 0, this.song.dur), gT = T, gBF = BF;
      T = t; BF = Math.floor(t * 8);
      try {
        drawActs(this.song, this.plan, t);
        cast(this.song, this.plan, t, docked);
        if (!docked) band(this.song, t);
      } finally { T = gT; BF = gBF; }
      if (docked) this.dock(t, title); else this.chrome(t);
    }

    /* ---------- the portrait dock: karaoke, controls and the strip below the stage ---------- */
    dock(t, title) {
      const S = this.song, pl = this.plan;
      const mmss = s => Math.floor(s / 60) + ':' + String(Math.floor(s % 60)).padStart(2, '0');
      Dock.head(title, `${mmss(t)} / ${mmss(S.dur)}`);
      const { cur, next } = lyricAt(S, t);
      const sec = (cur || next || {}).section;
      const lastE = S.items.length ? S.items[S.items.length - 1].e : 0;
      if (cur) {
        const key = cur.i + ':' + cur.s;
        if (!this._dk || this._dk.key !== key) {
          const hk = hookIdx(cur);
          this._dk = { key, words: cur.words.map((w, j) => ({ w: w.w, s: w.s, e: w.e, hook: hk.has(j) })) };
        }
        Dock.karaoke({ key, words: this._dk.words, t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '' });
      } else Dock.karaoke({ key: 'gap', words: [], t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '',
        note: t > lastE && S.items.length ? endNote() : this.startedOnce || this.frozen ? '♪ ♫ ♪' : 'loading the jingle…' });
      Dock.text('credit', ['♪ ' + CREDIT], { cls: 'small center' });
      if (this.canFinish() && !this.ended) Dock.buttons('jg-fin', [{ label: 'Finish', color: PN.redpen, onClick: () => this.finish() }]);
      if (this.ended && Game.current() === this) {
        Dock.buttons('jg-end', [{ label: 'Play it again', onClick: () => this.restart() },
          { label: 'Back to the map', color: PN.ink, onClick: () => this.back() }]);
      }
      Dock.strip({ value: t, total: S.dur, ticks: pl.ticks, label: 'jingle position', onSeek: (v, phase) => this.scrubTo(v, phase) });
      this.dockBar();
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended && !this.scrub) this.pausedMark(540, 150);
    }
    dockBar() {
      if (this.song && !this.ended) {
        Dock.button({ id: 'restart', glyph: '↺', label: 'restart', aria: 'play the jingle from the start', onClick: () => this.restart() });
        Dock.button({ id: 'play', glyph: this.playing ? '❚❚' : '▶', label: this.playing ? 'pause' : 'play', aria: this.playing ? 'pause' : 'play', primary: true, onClick: () => this.toggle() });
      }
      Dock.button({ id: 'map', glyph: '⌂', label: 'map', aria: 'back to the map', onClick: () => this.back() });
    }
    scrubTo(v, phase) {
      if (!this.song) return;
      const pos = clamp(v, 0, this.song.dur - 0.05);
      if (phase === 'start') { this.scrub = { was: this.playing || (this.scrub && this.scrub.was) }; if (this.playing) this.pause(); this.ended = false; }
      this.pos = pos;
      if (phase === 'end') { const was = this.scrub ? this.scrub.was : false; this.scrub = null; if (was) this.play(this.pos); }
    }
    /* the paused mark: a white wash and an ink circle with a drawn triangle */
    pausedMark(y, r) {
      g.save(); g.fillStyle = 'rgba(252,252,248,0.55)'; g.fillRect(0, 0, 1920, UI.docked() ? 1080 : BAND_Y); g.restore();
      g.save(); g.fillStyle = PN.ink; g.beginPath(); g.arc(960, y, r, 0, TAU); g.fill();
      g.fillStyle = PN.page; g.beginPath(); g.moveTo(960 - r * 0.3, y - r * 0.52); g.lineTo(960 + r * 0.52, y); g.lineTo(960 - r * 0.3, y + r * 0.52); g.closePath(); g.fill(); g.restore();
    }

    chrome(t) {
      const t0 = this.enteredAt, S = this.song;
      const intro = t == null || (this.plan && this.plan.acts.length > 1 && t < this.plan.acts[1].at);   // the intro page writes the big title itself
      if (!intro) NB.write(TITLE, 190, 124, { size: 46, weight: 700, t0: -1e9 });
      NB.write(CREDIT, 190, intro ? 112 : 176, { font: 'ui', size: 26, color: PN.pencil, t0: t0 + 0.4, dur: 0.3 });
      const m = UI.hudSlot(0), r = UI.hudSlot(1), pl = UI.hudSlot(2), fx = { r: m.r, fixed: true };
      if (UI.iconButton('jg-map', '⌂', m.x, m.y, fx)) { this.back(); return; }
      if (S) {
        if (UI.iconButton('jg-restart', '↺', r.x, r.y, fx)) this.restart();
        if (UI.iconButton('jg-play', this.playing ? '❚❚' : '▶', pl.x, pl.y, { ...fx, primary: true })) this.toggle();
      }
      if (t == null) return;
      // Finish: once ≥ 90% of the song has been heard (below the HUD, top right)
      this._finBox = null;
      if (this.canFinish() && !this.ended) {
        const w = UI.tap(250), h = UI.tap(84), fy = m.y + m.r + 30 + h / 2, fxp = m.x + m.r - w / 2;
        this._finBox = { x: fxp, y: fy, w, h };
        if (UI.button('jg-finish', 'Finish', fxp, fy, { w, h, size: UI.text(40), primary: true })) { this.finish(); return; }
      }
      const zone = this.stripZone(), touch = zone > 32;
      if (!this.ended) UI.target('jg-strip', 960, 1080 - zone / 2, 1920, zone);
      const sh = touch ? 18 : 10, sy = 1080 - sh;
      g.save();
      g.fillStyle = PN.pencilLight; g.fillRect(0, sy, 1920, sh);
      g.fillStyle = PN.ink; g.fillRect(0, sy, 1920 * t / S.dur, sh);
      g.fillStyle = PN.redpen; this.plan.ticks.forEach(b => g.fillRect(1920 * b / S.dur - 2, sy - 6, 4, sh + 6));
      g.restore();
      if (touch || this.scrub) NB.pencil(clamp(1920 * t / S.dur, 26, 1894), sy, touch ? 0.9 : 0.6);
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended) {
        this.pausedMark(430, 100);
        NB.write(UI.hint('paused · click or Space to play', 'paused · tap to play'), 960, 590, { font: 'ui', size: 44, weight: 700, align: 'center' });
      }
      if (!this.startedOnce && !this.frozen) NB.write('loading the jingle…', 960, 590, { font: 'ui', size: 44, color: PN.pencil, align: 'center', t0: this.enteredAt + 0.3 });
      if (this.ended && Game.current() === this) {
        g.save(); g.fillStyle = 'rgba(252,252,248,0.6)'; g.fillRect(0, 380, 1920, 360); g.restore();
        if (UI.button('jg-again', 'Play it again', 700, 560, { w: 440, h: 110, size: 50, secondary: true })) this.restart();
        if (UI.button('jg-back', 'Back to the map', 1220, 560, { w: 440, h: 110, size: 50, primary: true })) this.back();
      }
    }
  }
  Jingle.STEP = STEP;
  Jingle.Song = Song;
  Jingle.plan = plan;
  Jingle.nums = nums;
  Jingle.balanceAt = balanceAt;
  Jingle.KEY = KEY;
  Levels.jingle = Jingle;
})();
